import type { BandTarget } from './bandTypes';
import {
  nextSort as nextSortOf,
  sortIndicator as sortIndicatorOf,
  sortRows,
  type SortState,
} from './tableSort';

/**
 * 저점매수(BAND) 화면 표시 helper. (docs/impl/85)
 *
 * BandPanel 에서 뺀 이유 - 서버를 부르지 않는 순수 함수라
 * 컴포넌트 없이 그대로 테스트한다. (barcodeDisplay 와 같은 방침)
 *
 * 정렬 규칙 자체는 tableSort.ts 로 옮겼다(2026-08-28, 섹터 표에도
 * 정렬이 생기며 공통화). 여기는 감시 대상 전용 키와 값 추출만 남는다.
 */

/** 감시 대상 표에서 정렬할 수 있는 열. '종목'(순위)은 기본 정렬이라 여기 없다. */
export type TargetSortKey = 'current' | 'band' | 'distance';

export type TargetSort = SortState<TargetSortKey>;

/** 열을 눌렀을 때의 다음 정렬 상태. 오름 → 내림 → 기본(순위순). */
export const nextSort: (sort: TargetSort, key: TargetSortKey) => TargetSort = nextSortOf;

/** 정렬 표시자. 열 머리에 붙는다. */
export const sortIndicator: (sort: TargetSort, key: TargetSortKey) => string = sortIndicatorOf;

/**
 * 감시 대상 정렬. 기본 순서는 왕복성 순위, 값 없는 행은 항상 맨 아래.
 * (규칙과 이유는 tableSort.sortRows 주석 참조)
 */
export function sortTargets(targets: BandTarget[], sort: TargetSort): BandTarget[] {
  return sortRows(
    targets,
    sort,
    (t, key) => {
      switch (key) {
        case 'current':
          return t.currentPrice;
        case 'band':
          return t.bandPrice;
        default:
          return t.distancePercent;
      }
    },
    (a, b) => a.swingRank - b.swingRank,
  );
}

/**
 * 감시 대상 한 종목이 매수 기준가에서 몇 % 위에 있나. (2026-09-12 공통화)
 *
 * 🔴 서버 값(distancePercent)을 그대로 쓰지 않는다
 *   그것은 2분 주기 상태 응답의 현재가로 잰 값이다. 화면은 15초 폴링 시세를
 *   따로 갖고 있어, 현재가만 새것이고 거리만 헌것인 줄이 생긴다.
 *   살아 있는 현재가가 있으면 그것으로 다시 잰다.
 *
 * 표의 행(TargetRow)과 카드 위의 요약이 둘 다 이 값을 쓴다 - 한 곳에서 계산해야
 * "요약은 8종목인데 배지는 7개" 같은 어긋남이 안 생긴다. (CLAUDE.md 5.2)
 *
 * @param livePrice 15초 폴링 시세. 없으면 상태 응답의 현재가를 쓴다
 * @returns 양수면 기준가 위, 음수·0이면 매수 구간. 현재가를 모르면 null
 */
export function targetDistancePercent(
  target: BandTarget,
  livePrice: number | null,
): number | null {
  const current = livePrice ?? target.currentPrice;
  if (current === null || target.bandPrice === 0) {
    return target.distancePercent;
  }
  return ((current - target.bandPrice) / target.bandPrice) * 100;
}

/** 매수 기준가 아래로 내려왔나. 거리를 모르면 false - 모르는 것을 매수 구간으로 세지 않는다. */
export function isInBand(distancePercent: number | null): boolean {
  return distancePercent !== null && distancePercent <= 0;
}

/**
 * 감시 대상 한 종목의 지금 매수 상태. (2026-09-19 사용자 요청, I-62)
 *
 *   buy        매수 구간이고 살 수 있다 - 예수금만 있으면 이번 분에 산다
 *   watch      아직 매수 기준가 위다
 *   held       이미 들고 있다 - 새로 사지 않는다(추가 매수는 보유 표의 사다리가 따로 본다)
 *   belowStop  이미 손절선 아래다 - 사자마자 파는 매수라 사지 않는다(3회차부터)
 *   outOfRank  새로 사는 순위 밖이다 - 챌린저만 왕복성 상위 30 (3회차부터)
 *
 * 🔴 매매 코드(BandTradingService)와 같은 순서로 판정한다 - 보유 → 순위 → 기준가 → 손절선.
 *   화면이 "매수 구간" 이라고 하는데 실제로는 안 사는 줄이 생기지 않게 한다.
 */
export type TargetBuyState = 'buy' | 'watch' | 'held' | 'belowStop' | 'outOfRank';

export function targetBuyState(
  target: BandTarget,
  livePrice: number | null,
  maxSwingRank: number | null,
  held: boolean,
): TargetBuyState {
  if (held) {
    return 'held';
  }
  if (maxSwingRank !== null && target.swingRank > maxSwingRank) {
    return 'outOfRank';
  }
  if (!isInBand(targetDistancePercent(target, livePrice))) {
    return 'watch';
  }
  const current = livePrice ?? target.currentPrice;
  if (current !== null && target.stopPrice !== null && current <= target.stopPrice) {
    return 'belowStop';
  }
  return 'buy';
}

/**
 * 매수 순서(예상)로 줄 세운다. (2026-09-19 사용자 요청 - "매수 우선순위로 미리 보고 싶다")
 *
 * 🔴 운영이 실제로 사는 순서를 그대로 옮긴다 - "왕복성 순" 만으로 세우면 틀린다
 *   운영은 1분마다 감시해 **먼저 기준가에 닿은 종목부터** 산다. 순위는 같은 분에 여러 종목이
 *   닿았을 때만 쓰인다(BandTradingService 가 순위 순으로 돈다). 그래서
 *     ① 지금 매수 구간인 것 - 다음 감시에 순위 순으로 산다
 *     ② 아직 위에 있는 것 - 기준가에 가까운 것부터(먼저 닿을 가능성이 크다). 거리를 모르면 그 뒤
 *     ③ 보유 중 · 손절선 아래 · 순위 밖 - 새로 사지 않는다. 순위 순
 *   예수금이 모자라거나(저점매수 · 리스크) 자리가 차면(챌린저 8) 앞에서 끊긴다.
 *
 * @param livePriceOf 15초 폴링 시세. 없으면 상태 응답의 현재가를 쓴다
 */
export function sortByBuyOrder(
  targets: BandTarget[],
  livePriceOf: (stockId: number) => number | null,
  maxSwingRank: number | null,
  heldIds: ReadonlySet<number>,
): BandTarget[] {
  const group = (state: TargetBuyState) => (state === 'buy' ? 0 : state === 'watch' ? 1 : 2);
  const rows = targets.map((target) => {
    const live = livePriceOf(target.stockId);
    return {
      target,
      group: group(targetBuyState(target, live, maxSwingRank, heldIds.has(target.stockId))),
      distance: targetDistancePercent(target, live),
    };
  });
  return rows
    .sort((a, b) => {
      if (a.group !== b.group) {
        return a.group - b.group;
      }
      if (a.group === 1) {
        // 기준가에 가까운 것부터. 거리를 모르면 맨 뒤 - 모르는 것을 앞에 세우지 않는다
        if (a.distance === null || b.distance === null) {
          return a.distance === null ? (b.distance === null ? 0 : 1) : -1;
        }
        if (a.distance !== b.distance) {
          return a.distance - b.distance;
        }
      }
      return a.target.swingRank - b.target.swingRank;
    })
    .map((row) => row.target);
}
